import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { Alert } from 'antd';
import { SUBSCRIPTION_SIGN_UP_ROUTE } from '../../app/appConstants';
import { SubscriptionLoading } from '../../features/registration';
import {
  NewPostTrialDiscountSubscriptionCheckoutRequest,
  STRIPE_INTERVALS,
  StripeInterval,
  useCreateNewPostTrialDiscountCheckoutMutation,
} from '../../features/registration/registrationSlice';

export default function PostTrialDiscountPreCheckoutPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const interval = (searchParams.get('interval') ?? 'month') as StripeInterval;
  const userId = searchParams.get('userId');

  const [error, setError] = useState<string | null>(null);

  const [createDiscountSession] =
    useCreateNewPostTrialDiscountCheckoutMutation();

  useEffect(() => {
    if (!userId) {
      return;
    }
    if (!interval || !STRIPE_INTERVALS.includes(interval)) {
      setError(`Invalid interval: "${interval ?? 'null'}"`);
      return;
    }

    (async () => {
      // We use .../success here rather than .../pre-success because we already know the user is not a guest, and we know their userId
      const successUrl =
        `${window.location.protocol}//${window.location.host}` +
        `${SUBSCRIPTION_SIGN_UP_ROUTE}/success?plan=Pro&userId=${userId}&isTrialUpgrade=true`;

      const sessionParams: NewPostTrialDiscountSubscriptionCheckoutRequest = {
        user_id: userId,
        interval: interval,
        success_url: successUrl,
        refresh_url: window.location.href,
        cancel_url: window.location.href,
      };
      createDiscountSession(sessionParams)
        .unwrap()
        .then((redirect_url) => {
          if (redirect_url) {
            window.location.href = redirect_url;
          }
        })
        .catch((err) => {
          setError(
            'Error requesting discount: ' + err?.data?.detail ?? err?.data
          );
        });
    })();
  }, [createDiscountSession, interval, userId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>swimbooker+ | Subscribe Today!</title>
      </Helmet>
      <div className='grid h-full min-h-screen w-full place-content-center bg-gradient-to-b from-blue to-[#1B4C74] px-6 py-4 text-white min-[400px]:px-6'>
        <div className='mx-auto my-auto max-w-sm text-center'>
          {!userId ? (
            <Alert
              message='Something went wrong!'
              description='No user ID found.'
              type='error'
              showIcon
            />
          ) : error ? (
            <Alert
              message='Something went wrong!'
              description={
                <>
                  <p>{error}.</p>
                  <p>
                    Please try reloading the page or contact our support team!
                  </p>
                </>
              }
              type='error'
              showIcon
            />
          ) : (
            <SubscriptionLoading hideMessage />
          )}
        </div>
      </div>
    </>
  );
}
